import { useLocation } from 'react-router-dom'
import EnvironmentBadge from './EnvironmentBadge'
import { useTranslation } from 'react-i18next'
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'

import { useAuth } from '../hooks/useAuth'
import LanguageSwitcher from './LanguageSwitcher'
import ThemeToggle from './ThemeToggle'
import SystemIndicator from './SystemIndicator'
import SessionTimeoutModal from './SessionTimeoutModal'
import ConfirmCodeHost from './security/ConfirmCodeHost'
import DemoBanner from './DemoBanner'
import NavGroup from './nav/NavGroup'
import IconRail from './nav/IconRail'
import BottomNav from './nav/BottomNav'
import UserMenu from './nav/UserMenu'
import GroupTabs from './nav/GroupTabs'
import { getNavigation } from './nav/navigation'

/**
 * The application shell — VH-133
 *
 * Three layouts, not two:
 *
 *   < 768px    bottom navigation with a centre action button; everything
 *              else in the user menu at the top right, and a group's
 *              sub-pages as tabs on its pages (VH-297, HP-38: the ☰ drawer
 *              is gone, as in LifeBrain and FinanceHub)
 *   768–1279   a 72px icon rail that expands over the content on demand
 *   >= 1280px  the full 256px sidebar with collapsible groups
 *
 * The middle one is the addition. A fixed 256px sidebar on a 768px tablet
 * leaves roughly 512px of content, which crowds exactly the wide tables and
 * grids this redesign is meant to show off.
 *
 * All fifteen destinations survive at every size. The mockup's six-item
 * sidebar was a look, not an information architecture, and removing nine
 * working features to achieve it would have been a different feature request.
 */
export default function Layout({ children }) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const { t } = useTranslation()
  const navigation = getNavigation(t, user)

  return (
    <div className="min-h-screen bg-surface">
      {/* VH-79: demo environment banner (only rendered for the demo user) */}
      <DemoBanner />

      {/* --- Tablet rail (768–1279) --- */}
      <IconRail navigation={navigation} pathname={location.pathname} />

      {/* --- Desktop sidebar (>=1280) --- */}
      <div className="hidden xl:fixed xl:inset-y-0 xl:flex xl:w-64 xl:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto border-r border-outline-variant bg-surface-container pt-5 pb-4">
          <div className="flex flex-shrink-0 flex-col px-4">
            <h1 className="text-2xl font-bold text-gradient">VitaHub</h1>
            <p className="label-caps mt-1 text-gray-500">
              {t('nav.tagline')}
            </p>
          </div>
          <nav
            className="mt-8 flex-1 space-y-1 px-2"
            aria-label={t('nav.primary')}
          >
            {navigation.map((item) => (
              <NavGroup
                key={item.key}
                item={item}
                pathname={location.pathname}
              />
            ))}
          </nav>
        </div>
      </div>

      {/* --- Content --- */}
      <div className="flex min-h-screen flex-col md:pl-[72px] xl:pl-64">
        <header className="sticky top-0 z-30 flex h-16 flex-shrink-0 border-b border-outline-variant bg-surface-container/80 backdrop-blur-md">
          <div className="flex flex-1 items-center justify-between px-4">
            {/* VH-297: on a phone the name sits where the ☰ button was;
                nothing at the top left opens a menu any more. */}
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-gradient md:hidden">VitaHub</span>
              <EnvironmentBadge />
            </div>
            <div className="flex-1" />
            <div className="ml-4 flex items-center gap-3 md:ml-6">
              <div className="hidden text-sm text-gray-700 md:block">
                {t('common.welcome')}, {user?.username || user?.email}
              </div>
              {/* On a phone the theme and log-out live in the user menu. */}
              <div className="hidden md:block">
                <ThemeToggle />
              </div>
              <LanguageSwitcher />
              <button
                onClick={logout}
                className="hidden rounded-full p-1 text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface focus:ring-primary-500 md:block"
                title={t('common.logout')}
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6" />
              </button>
              <UserMenu />
            </div>
          </div>
        </header>

        {/* Bottom padding on phones clears the fixed bottom bar; without it
            the last row of every list sits underneath the navigation. */}
        {/* The content area. In light mode this is a grey tray carrying white
            cards. In dark mode VH-135 made it the page surface itself — one
            continuous purple — so `bg-canvas` resolves to `surface` there.

            `vh-canvas` no longer scopes a palette; VH-133 used it to flip text
            polarity for the light grey tray, and that mechanism went away with
            the tray. Kept as a stable hook for anything needing to target the
            content area. */}
        <main className="vh-canvas flex-1 bg-canvas text-on-canvas pb-24 md:pb-8">
          <GroupTabs />
          {children}
        </main>
      </div>

      {/* --- Phone bottom bar + action sheet --- */}
      <BottomNav />

      {/* System indicator for test environments */}
      <SystemIndicator />

      {/* VH-119: idle-timeout warning + auto-refresh. Mounted only in the
          authenticated shell, so it never runs on public/capture routes. */}
      <SessionTimeoutModal />
      {/* VH-285: "enter your code" before a sensitive action */}
      <ConfirmCodeHost />
    </div>
  )
}
